/**
 * Minhas solicitações (AIH-43).
 *
 * O que a pessoa pediu, em que pé está e qual o número do chamado. É a resposta
 * a "pedi acesso semana passada, e aí?" — sem ela a única saída é perguntar a
 * alguém, e o pedido some da vista assim que o painel fecha.
 *
 * ## Enquanto a Etapa 3 não fecha
 *
 * O número do chamado vem nulo, porque quem o obtém é o consumidor do
 * ServiceNow (AIH-42), que depende de acesso que ainda não temos. A tela diz
 * isso em vez de mostrar espaço em branco: "aguardando abertura do chamado".
 */
import { useCallback, useEffect, useState } from 'react';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { AppShell, Banner, StatsBar } from '../components/AppShell.jsx';
import { Toast } from '../components/Toast.jsx';
import { apiFetch, SessionError, goToSignIn } from '../api.js';
import { emitAccessChanged } from '../accessEvents.js';

/**
 * Cada estado ganha rótulo em português e cor.
 *
 * `QUEUED` é o que mais precisa de cuidado: significa "registramos, o chamado
 * ainda não abriu". Chamar isso de "pendente" faria a pessoa achar que está
 * esperando aprovação, quando ainda nem chegou a quem aprova.
 */
const STATUS_LABELS = {
  QUEUED: {
    label: 'Registrada',
    cls: 'pending',
    note: 'O chamado ainda será aberto no ServiceNow.',
  },
  SUBMITTED: {
    label: 'Em análise',
    cls: 'info',
    note: 'O chamado está aberto e aguarda aprovação.',
  },
  APPROVED: {
    label: 'Aprovada',
    cls: 'ok',
    note: 'O acesso já está liberado no portfólio.',
  },
  REJECTED: { label: 'Recusada', cls: 'neutral', note: null },
  CANCELLED: { label: 'Cancelada', cls: 'neutral', note: null },
  FAILED: {
    label: 'Falhou',
    cls: 'error',
    note: 'Não conseguimos abrir o chamado. A equipe de Inovação e IA foi avisada.',
  },
};

const shortDate = (iso) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

export function RequestList() {
  usePageMeta('AI Hub — minhas solicitações');

  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Busca as solicitações e, de quebra, descarta o cache de grupos do servidor.
   *
   * Havia um botão "Atualizar permissões" aqui (AIH-51). Ele existia porque o
   * cache de grupos dura dez minutos, e quem acabava de ser aprovado não queria
   * esperar — mas empurrava para o usuário uma decisão que é da plataforma:
   * ninguém sabe que precisa clicar, nem por quê.
   *
   * Agora a atualização acontece sozinha, sempre que esta tela carrega. A
   * falha é engolida de propósito: se a resolução de grupos não responder, a
   * lista de solicitações ainda vale e é o que a pessoa veio ver.
   */
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      await apiFetch('/api/me/refresh-permissions', { method: 'POST' }).catch(() => {});
      const r = await apiFetch('/api/requests');
      setList((antes) => {
        // Um pedido que saiu de aberto foi resolvido: o portfólio ainda mostra
        // o estado velho, e é esta tela quem descobre primeiro quando ela está
        // aberta. Sem isto, voltar para os Agentes mostraria "Sem acesso".
        const abertos = new Set(
          (antes ?? [])
            .filter((x) => x.status === 'QUEUED' || x.status === 'SUBMITTED')
            .map((x) => x.id),
        );
        const resolvido = r.requests.some(
          (x) => abertos.has(x.id) && x.status !== 'QUEUED' && x.status !== 'SUBMITTED',
        );
        if (resolvido) emitAccessChanged();
        return r.requests;
      });
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openRequests = (list ?? []).filter(
    (s) => s.status === 'QUEUED' || s.status === 'SUBMITTED',
  );

  /**
   * Enquanto houver pedido em aberto, a tela se atualiza sozinha.
   *
   * É a tela em que a pessoa fica esperando: sem isso ela ficaria olhando
   * "aguardando abertura" que já virou "aprovada" no servidor, e recarregaria
   * a página para descobrir. O laço para quando não há mais nada em aberto —
   * consultar para sempre uma tela que não muda é desperdício.
   *
   * `silent` para a lista não piscar "Carregando…" a cada dez segundos.
   */
  useEffect(() => {
    if (openRequests.length === 0) return undefined;
    const timer = setInterval(() => {
      if (!document.hidden) load({ silent: true });
    }, 10_000);
    return () => clearInterval(timer);
  }, [openRequests.length, load]);

  return (
    <AppShell>
      <Banner title="Minhas solicitações">Acompanhe o andamento dos acessos que você pediu.</Banner>

      <StatsBar
        items={[
          { label: 'Solicitações', value: list?.length ?? '—' },
          { label: 'Em aberto', value: list ? openRequests.length : '—' },
        ]}
      />

      <div className="list-panel">
        <div className="tabs-row">
          <div className="tabs">
            <span className="section-label">Histórico</span>
          </div>
          {openRequests.length > 0 && (
            <span className="live-hint" role="status">
              Acompanhando em tempo real…
            </span>
          )}
        </div>

        <div className="request-list">
          {loading && <p className="state-message">Carregando…</p>}

          {error && (
            <div className="state-message error" role="alert">
              <p>{error}</p>
              <button className="button secondary" type="button" onClick={load}>
                Tentar de novo
              </button>
            </div>
          )}

          {!loading && !error && list?.length === 0 && (
            <p className="state-message">
              Você ainda não solicitou acesso a nada. Abra o portfólio e peça o que precisar.
            </p>
          )}

          {!loading &&
            !error &&
            list?.map((s) => {
              const e = STATUS_LABELS[s.status] ?? { label: s.status, cls: 'neutral', note: null };
              return (
                <article className="request-row" key={s.id}>
                  <div className="request-info">
                    <div className="request-item">{s.itemName ?? s.itemId}</div>
                    <div className="request-meta">
                      Pedido em {shortDate(s.createdAt)}
                      {s.itemKind && ` · ${s.itemKind === 'AGENT' ? 'Agente' : 'Sistema'}`}
                    </div>
                    {s.justification && (
                      <div className="request-justification">“{s.justification}”</div>
                    )}
                    {e.note && <div className="request-note">{e.note}</div>}
                  </div>

                  <div className="request-right">
                    {/* O número é o que a pessoa cita ao cobrar. Sem ele, dizer
                      que está "aguardando" é mais honesto que deixar vazio. */}
                    {s.ticketNumber ? (
                      <span className="ticket-number">{s.ticketNumber}</span>
                    ) : (
                      <span className="ticket-number ausente">aguardando abertura</span>
                    )}
                    <span className={`status-pill ${e.cls}`}>{e.label}</span>
                  </div>
                </article>
              );
            })}
        </div>
      </div>

      <Toast />
    </AppShell>
  );
}
